import { Trans } from '@lingui/react/macro';
import { Outlet } from 'react-router';

import { useAudienceOptionsPrefetch } from '@/api/audience';
import { useCurrentInstance } from '@/api/organization';
import Error404 from '@/components/errors/Error404';
import PageError from '@/components/errors/PageError';
import { GlobalLoader } from '@/components/GlobalLoader';
import { Layout, PageHeader } from '@/components/page';
import { SidebarProvider } from '@/components/Sidebar';
import { PAGE_WIDTH_NO_SIDEBAR } from '@/constants/dimensions';
import { useMe } from '@/providers/User';

import LoginAsNotification from './LoginAsNotification';
import NotificationWrapper from './NotificationWrapper';
import { Sidebar } from './Sidebar';
import TagNotFoundNotification from './TagNotFoundNotification';

const { Content } = Layout;

export const Home = () => {
  const user = useMe();
  const { instance, isLoading, error } = useCurrentInstance();
  useAudienceOptionsPrefetch();
  const foreignSuperuserInstance = user?.isC99Superuser && user.currentOrg.isForeign;

  if (isLoading) {
    return <GlobalLoader />;
  }

  if (error) {
    if (error.is404()) {
      return (
        <Error404
          title={<Trans>Unable to load the instance</Trans>}
          subtitle={
            <Trans>
              This can happen if the instance doesn't exist or you don't have access to it.
              <p>
                If you don't have access to an instance, please contact your administrator for an
                invite. Check that you typed the address correctly, go back to your previous page or
                return to the home page.
              </p>
            </Trans>
          }
        />
      );
    }
    return (
      <PageError
        message={<Trans>Unable to load the instance</Trans>}
        detail={
          <Trans>
            Please refresh the page. If this error continues to happen please contact support.
          </Trans>
        }
      />
    );
  }

  return (
    <SidebarProvider
      mobileBreakpoint={PAGE_WIDTH_NO_SIDEBAR}
      className="[--sidebar-width-mobile:0px] [--sidebar-width:0px] sm:[--sidebar-width-mobile:180px] sm:[--sidebar-width:180px] 2xl:[--sidebar-width-mobile:200px] 2xl:[--sidebar-width:200px]"
    >
      <Layout className="h-screen flex-row">
        <Sidebar />
        <Layout className="min-w-0 flex-1">
          <PageHeader />
          <Content className="flex flex-col overflow-auto bg-white">
            {foreignSuperuserInstance && (
              <LoginAsNotification instanceId={user?.currentOrg.instanceId} />
            )}
            {!instance?.tagInstallationDate && <TagNotFoundNotification />}
            <NotificationWrapper />
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </SidebarProvider>
  );
};
